import { Form, Formik } from 'formik';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import validationSchema from './validation-schema';
import { toast } from 'react-toastify';
import PmiTitle from 'shell/components/shared/title';
import { AuthorizationVoteCrumbs } from 'front-office/constants/authorization-vote-crumbs';
import PrintAuthorizationVoteForm from 'front-office/components/authorization-vote/detail/form';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { __ELECTORAL_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { AuthorizationVote } from 'shell/models/person/electoral-card';
import {
  AuthorizationVoteFormVM,
  ElectoralDataExtendedVM,
} from 'front-office/view-models/authorization-vote';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { transformFamilyCoexistence } from 'shell/utils/consult-person/response-transformer/family/family-coexistence';
import { transformCitizenship } from 'shell/utils/consult-person/response-transformer/registry-office/citizenship';
import { DateTime } from 'luxon';
import { transformerVoterHistoric } from 'electoral/utils/transformer-voter-detail';

const PrintAuthorizationVotePage = (): React.ReactElement => {
  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const navigate = useNavigate();

  const onExit = useCallback(() => {
    navigate('/sportello/autorizzazione-voto');
  }, [navigate]);

  const onSubmit = useCallback(() => {
    toast.success('Operazione eseguita con successo');
  }, []);

  useEffect(() => {
    const [subject] =
      filterTable.values?.subject?.dati3002?.risposta3002OK?.listaSoggetti?.datiSoggetto || [];

    if (!subject) {
      onExit();
    }
  }, []);

  const [electoralPersonData, getElectoralPersonData] = useHttpFetch<
    HttpResponse<AuthorizationVote>,
    ElectoralDataExtendedVM
  >(`${__ELECTORAL_API_URL__}/persona/autorizzazione-al-voto/controlli`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {
        codiceFiscale: filterTable.values?.subject?.fiscalCode || undefined,
      },
    },
    transformer: (response) => {
      return {
        ...transformerVoterHistoric(response.data.payload),
        electoralDataModel: response.data.payload,
        matricola: response.data.payload.matricola,
        flagCertificatoAutorizzazione: response.data.payload.flagCertificatoAutorizzazione,
        consultazione: response.data.payload.consultazione,
        dataDa: response.data.payload.dataDa
          ? DateTime.fromISO(response.data.payload.dataDa)
          : null,
        datiAutorizzazioneAlVoto: response.data.payload.datiAutorizzazioneAlVoto,
        dataA: response.data.payload.dataA ? DateTime.fromISO(response.data.payload.dataA) : null,
        tipoConsultazione: response.data.payload?.tipoConsultazione,
      };
    },
  });

  const dataANPR: AuthorizationVoteFormVM = useMemo(() => {
    const electoralPerson = electoralPersonData.data as ElectoralDataExtendedVM;
    const person = transformPerson(
      filterTable.values?.subject?.dati3002?.risposta3002OK.listaSoggetti.datiSoggetto[0] || {}
    );
    const family = transformFamilyCoexistence(
      filterTable.values?.subject?.dati3002?.risposta3002OK.listaSoggetti.datiSoggetto[0] || {}
    );

    const citizenship = transformCitizenship(
      filterTable.values?.subject?.dati3002?.risposta3002OK.listaSoggetti.datiSoggetto[0] || {}
    );

    return {
      authorizationData: !electoralPerson?.datiAutorizzazioneAlVoto
        ? {
            isMilanPlace: true,
            milan: {
              requestDate: DateTime.now(),
              section: '',
            },
            outsideMilan: null,
          }
        : {
            isMilanPlace: !!electoralPerson?.datiAutorizzazioneAlVoto?.luogoCuraMilano,
            milan: electoralPerson?.datiAutorizzazioneAlVoto?.luogoCuraMilano
              ? {
                  requestDate: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano
                    ?.dataRichiesta
                    ? DateTime.fromISO(
                        electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.dataRichiesta
                      )
                    : DateTime.now(),
                  collegioCamera:
                    electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.collegioCamera?.toString(),
                  collegioProvinciale:
                    electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.collegioProvinciale?.toString(),
                  collegioSenato:
                    electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.collegioSenato?.toString(),
                  description:
                    electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.descrizione,
                  section:
                    electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.sezione?.toString(),
                  zone: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraMilano?.zona,
                }
              : null,
            outsideMilan: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano
              ? {
                  requestDate: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano
                    ?.dataRichiesta
                    ? DateTime.fromISO(
                        electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano
                          ?.dataRichiesta
                      )
                    : DateTime.now(),
                  place: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano?.luogo,
                  municipality: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano
                    ?.comune
                    ? {
                        code: electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano?.comune
                          ?.key?.codice,
                        description:
                          electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano?.comune
                            ?.nome || '',
                        id:
                          electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano?.comune?.codIstat?.toString() ||
                          '',
                        province:
                          electoralPerson.datiAutorizzazioneAlVoto?.luogoCuraFuoriMilano?.comune
                            .provincia || '',
                      }
                    : null,
                }
              : null,
          },
      isAuthorized: electoralPerson?.flagCertificatoAutorizzazione,
      family,
      citizenship,
      person,
      electoralData: electoralPerson,
    };
  }, [electoralPersonData.data]);

  const isLoading = electoralPersonData?.isLoading || electoralPersonData?.pristine;

  return (
    <Fragment>
      <Breadcrumbs items={AuthorizationVoteCrumbs} />
      <PmiTitle title="Autorizzazione al voto"></PmiTitle>
      {isLoading ? (
        <></>
      ) : (
        <Formik initialValues={dataANPR} validationSchema={validationSchema} onSubmit={onSubmit}>
          <Form>
            <PrintAuthorizationVoteForm
              getElectoralPersonData={getElectoralPersonData}
              onExit={onExit}
            />
          </Form>
        </Formik>
      )}
    </Fragment>
  );
};

PrintAuthorizationVotePage.displayName = 'Print Authorization Vote page component';

export default PrintAuthorizationVotePage;
